import { createContext, useContext, useEffect } from "react";

/**
 * The public origin every canonical URL names, whichever host served the page:
 * www (which the tunnel hands nginx as the apex, so nginx cannot redirect it),
 * UAT, the Tailscale name. Pointing all of them here is what tells Google which
 * copy is the original.
 */
export const CANONICAL_ORIGIN = "https://easyplanner.ro";

/**
 * `/Terms/?x=1#top` → `https://easyplanner.ro/terms`. No query, no fragment, no
 * trailing slash, lower case: React Router and the slug lookup both ignore
 * case, so every spelling resolves, and only one of them may be the page's own.
 */
export const canonicalUrl = (path) => {
  const bare = String(path).split(/[?#]/)[0].replace(/^\/+|\/+$/g, "");
  return `${CANONICAL_ORIGIN}/${bare.toLowerCase()}`;
};

/**
 * Where the hook writes when there is no document: the build's prerender
 * (scripts/prerender.mjs) renders each public page to a string in Node, where
 * effects never run, and copies what lands here into that page's <head>
 * itself. In the browser nothing provides it and the hook writes the DOM.
 */
export const PageMetaCollector = createContext(null);

/**
 * Per-page <head> for the public site: the title, the description, the
 * canonical URL and a noindex — what a search engine reads to tell one page
 * from another.
 *
 * The SPA answers every path with the same index.html, so every page Google
 * rendered carried the same title and description and none named its own URL.
 * Search Console then filed the booking page /discover links to —
 * `/salon?service=5&resource=1&date=…&time=…`, one URL per slot — as
 * "duplicate without user-selected canonical" of `/salon`. A canonical says
 * which URL the page is.
 *
 * Only the fields passed are touched, and each is put back as it was when the
 * page unmounts. So the route in App.jsx can own a static page's canonical
 * while the component, which has `t`, owns its title; and a page that sets
 * nothing (the dashboard) never inherits the last page's canonical. Two callers
 * setting the same field on one page is the mistake: two canonicals, and Google
 * ignores both.
 *
 * Google reads all of this from the rendered DOM. That is why index.html has
 * no canonical: it would be the same on every page, the one wrong answer. A
 * crawler that runs no JavaScript reads it from the HTML the prerender wrote
 * for the page (or, for a business's page, the HTML the API composed), which
 * carries the same values.
 */
export const usePageMeta = ({ title, description, path, noindex = false } = {}) => {
  const collected = useContext(PageMetaCollector);
  if (collected) {
    if (title) collected.title = title;
    if (description) collected.description = description;
    if (path) collected.canonical = canonicalUrl(path);
    if (noindex) collected.noindex = true;
  }

  // "Put back as it was" means the site's own value, not the first page's: a
  // prerendered or API-written page arrives with its own title and
  // description already in place and keeps the defaults in data-default, so
  // leaving it for a page that sets none must restore those.
  useEffect(() => {
    if (!title) return undefined;
    const previous =
      document.head.querySelector("title")?.getAttribute("data-default") ?? document.title;
    document.title = title;
    return () => {
      document.title = previous;
    };
  }, [title]);

  useEffect(() => {
    const meta = description && document.head.querySelector('meta[name="description"]');
    if (!meta) return undefined;
    const previous = meta.getAttribute("data-default") ?? meta.getAttribute("content");
    meta.setAttribute("content", description);
    return () => meta.setAttribute("content", previous);
  }, [description]);

  const href = path ? canonicalUrl(path) : null;
  useEffect(() => {
    if (!href) return undefined;
    const link = document.createElement("link");
    link.rel = "canonical";
    link.href = href;
    document.head.appendChild(link);
    return () => link.remove();
  }, [href]);

  // For the pages a search result has no business pointing at: token links,
  // the dashboard. Google honours a noindex it finds while rendering.
  useEffect(() => {
    if (!noindex) return undefined;
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex";
    document.head.appendChild(meta);
    return () => meta.remove();
  }, [noindex]);
};

/**
 * Removes the canonical a prerendered or API-written page arrived with, before
 * React renders. From then on the hook is the only thing that writes one —
 * otherwise the page would carry two (Google ignores both), and the server's
 * would stay behind on every page the visitor navigates to after it.
 */
export const dropServerCanonical = () => {
  document.head.querySelectorAll('link[rel="canonical"]').forEach((link) => link.remove());
};

/** The hook as an element, for a route in App.jsx to drop beside its page. */
export const PageMeta = (props) => {
  usePageMeta(props);
  return null;
};
